import { useEffect, useState } from 'react';
import {
  ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  CartesianGrid, ReferenceLine,
} from 'recharts';
import { execApi } from '../../../services/api';

// The business dashboard: the approved 2026 plan vs reality. Revived 2026-08-02 —
// the mock drill was purged 2026-07-13; THIS one has no mock in it. Targets come
// from dashboard_app.business_plan, the estimated actual revenue from the same
// code path as the hub's revenue tile (plan tariffs: subscription at CM intake,
// FU per meeting, standby pro-rated), so the two screens cannot disagree.
//
// Expenses are plan-only — there is no live expense actual, and the page says so
// instead of pretending.

interface FinMonth {
  period: string; label: string;
  revenue_target: number; revenue_estimated: number | null; revenue_forecast: number;
  expense_target: number; gross_target: number; gross_pct_target: number;
  operating_target: number; breakeven: number;
  backlog_hours: number; cm_intakes_target: number; partial: boolean;
}
interface FinAnnual {
  revenue: number; cost_of_revenue: number; gross: number; gross_pct: number;
  fixed_total: number; operating: number; breakeven: number;
  safety_margin: number; safety_pct: number;
}
interface Drill {
  plan_version: string; months: FinMonth[]; annual: FinAnnual;
  ytd_target: number; ytd_estimated: number;
}

// Repo chart palette (same as the hub tiles): target gray, actual green.
const C_TARGET = '#94A3B8';
const C_ACTUAL = '#10B981';
const C_NEG = '#EF4444';
const C_BACKLOG = '#8B5CF6';
const C_BE = '#64748B';

const fmtM = (n: number) => {
  if (Math.abs(n) >= 1_000_000) return `₪${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `₪${Math.round(n / 1_000).toLocaleString()}K`;
  return `₪${Math.round(n).toLocaleString()}`;
};
const fmtFull = (n: number) => `₪${Math.round(n).toLocaleString()}`;
const fmtPct = (n: number) => `${(n * 100).toFixed(2)}%`;

function Kpi({ label, value, sub, tone }: {
  label: string; value: string; sub?: string; tone?: 'good' | 'bad';
}) {
  const toneCls = tone === 'good' ? 'text-green-600' : tone === 'bad' ? 'text-red-600' : 'text-gray-900';
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${toneCls}`} dir="ltr">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

const tooltipStyle = {
  background: '#101D35', border: '1px solid #243457', borderRadius: 8,
  color: '#f1f5f9', fontSize: 12, direction: 'rtl' as const,
};

export default function ExecFinancial() {
  const [data, setData] = useState<Drill | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    execApi.getFinancial()
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  if (error) return <div className="text-center text-gray-500 py-16">שגיאה בטעינת הנתונים</div>;
  if (!data) return <div className="text-center text-gray-500 py-16">טוען…</div>;

  const a = data.annual;
  const rows = data.months.map((m) => ({
    ...m,
    estimated: m.revenue_estimated,
    // Measured operating result = estimated revenue − plan expenses. Complete months
    // only: an accruing month against a full month of expenses would just read "red".
    operating_est: (m.revenue_estimated != null && !m.partial)
      ? m.revenue_estimated - m.expense_target : null,
    gross_pct100: +(m.gross_pct_target * 100).toFixed(2),
  }));
  const h2 = rows.filter((m) => m.backlog_hours > 0);
  const ytdGap = data.ytd_estimated - data.ytd_target;

  return (
    <div className="space-y-6" dir="rtl">
      {/* ── Annual KPI strip ── */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-800">תקציב 2026 — שנתי</h2>
          <span className="text-xs text-gray-500">גרסת תוכנית: {data.plan_version}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Kpi label="מחזור תקציב" value={fmtM(a.revenue)} />
          <Kpi label="רווח גולמי" value={fmtM(a.gross)} sub={fmtPct(a.gross_pct)} />
          <Kpi label="הוצאות קבועות" value={fmtM(a.fixed_total)} sub="כוח אדם, ספקים ותפעול" />
          <Kpi label="תוצאה תפעולית" value={fmtM(a.operating)} tone={a.operating < 0 ? 'bad' : 'good'} />
          <Kpi label="נקודת איזון שנתית" value={fmtM(a.breakeven)} sub={`מרווח ${fmtPct(a.safety_pct)}`} tone={a.safety_margin < 0 ? 'bad' : 'good'} />
          <Kpi
            label="פער YTD מול תוכנית" value={fmtM(ytdGap)}
            sub={`אומדן ${fmtM(data.ytd_estimated)} מול יעד ${fmtM(data.ytd_target)}`}
            tone={ytdGap < 0 ? 'bad' : 'good'}
          />
        </div>
      </div>

      {/* ── Revenue: plan vs estimated actual ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-800 mb-1">הכנסות — תוכנית מול אומדן בפועל</h3>
        <p className="text-xs text-gray-500 mb-3">
          אומדן = פגישות שבוצעו × תעריפי התוכנית (מנוי אבחוני נרשם באינטייק מנהל טיפול, מעקבים פר פגישה) + כוננות.
          הקו המקווקו — מחזור נקודת האיזון החודשית.
        </p>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748B' }} />
            <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}K`} tick={{ fontSize: 11, fill: '#64748B' }} width={44} orientation="right" />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(v: number, name: string) => [fmtFull(v), name]}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="revenue_target" name="יעד" fill={C_TARGET} radius={[3, 3, 0, 0]} />
            <Bar dataKey="estimated" name="בפועל (אומדן)" fill={C_ACTUAL} radius={[3, 3, 0, 0]} />
            <Line dataKey="breakeven" name="נקודת איזון" stroke={C_BE} strokeWidth={2} strokeDasharray="5 4" dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ── Operating result: plan vs measured (est revenue − plan expenses) ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="font-semibold text-gray-800 mb-1">הכנסות − הוצאות (תוצאה תפעולית) — תוכנית מול אומדן</h3>
        <p className="text-xs text-gray-500 mb-3">
          יעד = תוכנית; אומדן = הכנסות משוערות פחות יעד ההוצאות של החודש (חודשים שהושלמו בלבד — אין הוצאות בפועל).
          אוגוסט–ספטמבר אדומים בכוונה — זה מחיר חיסול הצבר.
        </p>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748B' }} />
            <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}K`} tick={{ fontSize: 11, fill: '#64748B' }} width={44} orientation="right" />
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number, name: string) => [fmtFull(v), name]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine y={0} stroke="#E2E8F0" />
            <Bar dataKey="operating_target" name="יעד" fill={C_TARGET} radius={[3, 3, 0, 0]} />
            <Bar dataKey="operating_est" name="אומדן" radius={[3, 3, 0, 0]}>
              {rows.map((m) => (
                <Cell key={m.period} fill={(m.operating_est ?? 0) >= 0 ? C_ACTUAL : C_NEG} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ── The backlog plan (H2) — small multiples, one story ── */}
      {h2.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-800 mb-1">תוכנית חיסול הצבר — יולי עד דצמבר</h3>
          <p className="text-xs text-gray-500 mb-3">
            שעות פסיכיאטר מופנות לצבר האינטייקים, וקליטת אינטייקי מנהלי טיפול נעצרת באוגוסט–ספטמבר
            (18 ואז 0). לכן השיעור הגולמי צונח ל-42.15% באוגוסט ו-34.98% בספטמבר וחוזר מנובמבר.
            השקעה חד-פעמית, לא הידרדרות.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">שעות צבר לפי חודש</div>
              <ResponsiveContainer width="100%" height={170}>
                <ComposedChart data={h2} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748B' }} width={34} orientation="right" />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v} שעות`, 'שעות צבר']} />
                  <Bar dataKey="backlog_hours" name="שעות צבר" fill={C_BACKLOG} radius={[3, 3, 0, 0]} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1">שיעור גולמי חודשי (%)</div>
              <ResponsiveContainer width="100%" height={170}>
                <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748B' }} width={34} orientation="right" />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v}%`, 'שיעור גולמי']} />
                  <Line dataKey="gross_pct100" name="שיעור גולמי" stroke={C_BE} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* ── Monthly table ── */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 overflow-x-auto">
        <h3 className="font-semibold text-gray-800 mb-1">פירוט חודשי</h3>
        <p className="text-xs text-gray-500 mb-3">
          הוצאות — יעד תוכנית בלבד: אין מקור חי להוצאות בפועל, ולכן אין עמודת ביצוע.
        </p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-200">
              <th className="text-right py-2 px-2">חודש</th>
              <th className="text-left py-2 px-2">הכנסות — יעד</th>
              <th className="text-left py-2 px-2">הכנסות — אומדן</th>
              <th className="text-left py-2 px-2">פער</th>
              <th className="text-left py-2 px-2">הוצאות — יעד</th>
              <th className="text-left py-2 px-2">גולמי %</th>
              <th className="text-left py-2 px-2">תפעולי — יעד</th>
              <th className="text-left py-2 px-2">נקודת איזון</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => {
              const gap = m.estimated == null ? null : m.estimated - m.revenue_target;
              return (
                <tr key={m.period} className={`border-b border-gray-100 ${m.partial ? 'bg-blue-50/50' : ''}`}>
                  <td className="py-1.5 px-2 font-medium text-gray-700">
                    {m.label}{m.partial && <span className="text-xs text-blue-500 mr-1">· מצטבר</span>}
                  </td>
                  <td className="py-1.5 px-2 text-left" dir="ltr">{fmtFull(m.revenue_target)}</td>
                  <td className="py-1.5 px-2 text-left" dir="ltr">{m.estimated == null ? '—' : fmtFull(m.estimated)}</td>
                  <td className={`py-1.5 px-2 text-left ${gap == null ? 'text-gray-500' : gap < 0 ? 'text-red-600' : 'text-green-600'}`} dir="ltr">
                    {gap == null ? '—' : fmtFull(gap)}
                  </td>
                  <td className="py-1.5 px-2 text-left" dir="ltr">{fmtFull(m.expense_target)}</td>
                  <td className="py-1.5 px-2 text-left" dir="ltr">{fmtPct(m.gross_pct_target)}</td>
                  <td className={`py-1.5 px-2 text-left ${m.operating_target < 0 ? 'text-red-600' : 'text-green-600'}`} dir="ltr">
                    {fmtFull(m.operating_target)}
                  </td>
                  <td className="py-1.5 px-2 text-left" dir="ltr">{fmtFull(m.breakeven)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
